<!-- src/views/Dashboard/ReportPanel.vue -->
<template>
  <div class="bg-white rounded-lg border border-gray-200 shadow-md overflow-hidden">
    <!-- 보고서 헤더 -->
    <div class="bg-slate-800 text-white px-6 py-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-xl font-bold">데이터 분석 보고서</h2>
           
        </div>
        <button
          @click="$emit('close')"
          class="text-white hover:bg-slate-700 rounded-full p-2 transition-colors"
          title="보고서 닫기"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- 보고서 내용 (카드형 레이아웃) -->
    <div class="p-6 bg-gray-50">
      <!-- 섹션 선택 화면 -->
      <div v-if="viewMode === 'select'" class="grid grid-cols-1 md:grid-cols-2 gap-4">

        <!-- 보고서 섹션 선택 카드 -->
        <div class="bg-white rounded-lg border border-gray-200 p-5 shadow-sm md:col-span-2">
          <h3 class="text-base font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
            보고서 섹션 선택
          </h3>

          <!-- 탭 -->
          <div class="mb-6">
            <div class="flex gap-2 border-b border-gray-200">
              <button
                v-for="tab in reportTabs"
                :key="tab.id"
                @click="activeTab = tab.id"
                :class="[
                  'px-4 py-2 text-sm font-medium transition-all relative',
                  activeTab === tab.id
                    ? 'text-violet-600 border-b-2 border-violet-600'
                    : 'text-gray-600 hover:text-gray-800'
                ]"
              >
                {{ tab.label }}
              </button>
            </div>
          </div>

          <!-- 보고서 제목 입력 -->
          <div class="mb-6">
            <label class="block text-sm font-semibold text-gray-700 mb-2 text-left">보고서 제목 <span class="text-red-500 text-xs font-bold">(필수)</span></label>
            <input
              v-model="reportTitle"
              type="text"
              placeholder="보고서 제목을 입력하세요"
              class="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
            />
          </div>

          <!-- 섹션 목록 또는 안내 메시지 -->
          <div v-if="reportSections.length > 0" class="space-y-6">
            <div
              v-for="section in reportSections"
              :key="section.id"
              class="space-y-3"
            >
              <h4 class="text-sm font-semibold text-gray-700 text-left">{{ section.id }}. {{ section.title }}</h4>
              <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div
                  v-for="subsection in section.subsections"
                  :key="subsection.id"
                  :class="[
                    'border rounded-lg p-4 transition-all',
                    subsection.unavailable
                      ? 'border-gray-200 bg-gray-100 opacity-60'
                      : subsection.selected
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-gray-200 bg-white hover:border-gray-300',
                    subsection.disabled && !subsection.unavailable ? 'opacity-75' : ''
                  ]"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex-1 min-w-0 text-left">
                      <div class="flex items-center gap-2 mb-1">
                        <h5 class="text-sm font-semibold text-gray-800">
                          {{ subsection.title }}
                        </h5>
                        <span v-if="subsection.unavailable" class="text-[10px] text-red-600 font-medium"></span>
                        <span v-else-if="subsection.disabled" class="text-[10px] text-violet-600 font-medium">(필수)</span>
                      </div>
                      <p class="text-xs text-gray-500 leading-relaxed ">{{ subsection.description }}</p>
                    </div>
                    <div class="flex-shrink-0">
                      <label :class="[
                        'relative inline-flex items-center',
                        subsection.unavailable || subsection.disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                      ]">
                        <input
                          type="checkbox"
                          :checked="subsection.selected"
                          :disabled="subsection.disabled || subsection.unavailable"
                          @change="toggleSection(section.id, subsection.id)"
                          class="sr-only peer"
                        >
                        <div :class="[
                          'w-11 h-6 rounded-full peer transition-colors',
                          subsection.unavailable
                            ? 'bg-gray-300 cursor-not-allowed'
                            : subsection.disabled
                              ? 'bg-violet-400 cursor-not-allowed'
                              : subsection.selected
                                ? 'bg-violet-600 peer-hover:bg-violet-700'
                                : 'bg-gray-300 peer-hover:bg-gray-400'
                        ]">
                          <div :class="[
                            'absolute top-[2px] left-[2px] bg-white w-5 h-5 rounded-full transition-transform',
                            subsection.selected ? 'transform translate-x-5' : ''
                          ]"></div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-12 text-gray-500">
            <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <p class="mt-4 text-sm font-medium">이 템플릿은 준비 중입니다</p>
            <p class="mt-1 text-xs">다른 탭을 선택해주세요</p>
          </div>
        </div>

        <!-- 선택된 보고서 섹션 미리보기 -->
        <div class="bg-white rounded-lg border border-gray-200 p-5 shadow-sm md:col-span-2 relative min-h-[300px] overflow-hidden">
          <h3 class="text-base font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
            선택된 보고서 섹션 ({{ selectedSections.length }}개)
          </h3>
          <div v-if="selectedSections.length > 0" class="space-y-3">
            <div
              v-for="(item, index) in selectedSections"
              :key="item.subsectionId"
              class="flex items-start p-3 bg-gray-50 rounded-md text-left"
            >
              <span class="inline-flex items-center justify-center w-6 h-6 bg-slate-800 text-white text-xs font-bold rounded-full mr-3 flex-shrink-0">
                {{ index + 1 }}
              </span>
              <div> 
                <p class="text-sm font-semibold text-gray-800 ">
                  {{ item.subsectionTitle }}
                </p>
                <p class="text-xs text-gray-500 mt-1">{{ item.sectionTitle }}</p>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-8 text-gray-500">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <p class="mt-3 text-sm">보고서에 포함할 섹션을 선택해주세요</p>
          </div>

          <!-- 보고서 생성 진행 상태 오버레이 -->
          <div
            v-if="isGenerating"
            class="absolute inset-0 bg-white bg-opacity-95 flex flex-col items-center justify-center rounded-lg z-10"
          >
            <div class="text-center w-full max-w-md px-6">
              <!-- 로딩 스피너 -->
              <div class="mb-6">
                <div class="inline-block animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-violet-600"></div>
              </div>

              <!-- 상태 정보 -->
              <div class="space-y-4">
                <h4 class="text-lg font-semibold text-gray-800">
                  보고서 생성 중...
                </h4>

                <!-- 진행률 바 -->
                <div v-if="taskStatus" class="w-full">
                  <div class="flex justify-between text-sm text-gray-600 mb-2">
                    <span>{{ getStatusText(taskStatus.status) }}</span>
                    <span>{{ taskStatus.progress || 0 }}%</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-3">
                    <div
                      class="bg-violet-600 h-3 rounded-full transition-all duration-500"
                      :style="{ width: `${taskStatus.progress || 0}%` }"
                    ></div>
                  </div>
                </div>

                <!-- 현재 단계 표시 -->
                <p v-if="taskStatus?.current_step" class="text-sm text-gray-500">
                  {{ taskStatus.current_step }}
                </p>

                <!-- Task ID 표시 -->
                <p v-if="currentTaskId" class="text-xs text-gray-400 mt-4">
                  Task ID: {{ currentTaskId }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 보고서 결과 화면 -->
      <div v-else-if="viewMode === 'result'" class="bg-white rounded-lg border border-gray-200 shadow-sm">
        <!-- 결과 헤더 -->
        <div class="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-800">생성된 보고서</h3>
          <button
            @click="goBackToSelect"
            class="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            섹션 선택으로 돌아가기
          </button>
        </div>

        <!-- 마크다운 렌더링 영역 -->
        <div class="p-6 max-h-[600px] overflow-y-auto result-content-box">
          <div
            class="markdown-body leading-relaxed"
            v-html="renderedMarkdown"
          ></div>
        </div>

        <!-- Task ID 정보 -->
        <div v-if="currentTaskId" class="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-400">
          Task ID: {{ currentTaskId }}
        </div>
      </div>
    </div>

    <!-- 보고서 푸터 -->
    <div class="bg-gray-100 px-6 py-3 border-t border-gray-200">
      <!-- 에러 메시지 -->
      <div v-if="errorMessage" class="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
        <p class="text-sm text-red-600">{{ errorMessage }}</p>
      </div>

      <!-- 섹션 선택 화면 버튼 -->
      <div v-if="viewMode === 'select'" class="flex justify-end gap-2">
        <button
          class="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
          @click="$emit('close')"
        >
          닫기
        </button>
        <button
          class="px-4 py-2 bg-slate-800 text-white text-sm rounded-md hover:bg-slate-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isLoading || isGenerating || selectedSections.length === 0"
          @click="generateReport"
        >
          <span v-if="isLoading || isGenerating" class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isGenerating ? '생성 진행 중...' : '생성 중...' }}
          </span>
          <span v-else>보고서 생성</span>
        </button>
      </div>

      <!-- 결과 화면 버튼 -->
      <div v-else-if="viewMode === 'result'" class="flex justify-end gap-2">
        <button
          class="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
          @click="goBackToSelect"
        >
          다시 생성
        </button>
        <button
          class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isDownloading"
          @click="downloadWord"
        >
          <svg v-if="isDownloading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          {{ isDownloading ? '다운로드 중...' : 'Word 다운로드' }}
        </button>
        <button
          class="px-4 py-2 bg-slate-800 text-white text-sm rounded-md hover:bg-slate-900 transition-colors"
          @click="$emit('close')"
        >
          닫기
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { marked } from 'marked'

const store = useStore()

// Store에서 폴링 상태 가져오기
const taskStatus = computed(() => store.getters['report/taskStatus'])
const isPolling = computed(() => store.getters['report/isPolling'])
const reportContent = computed(() => store.getters['report/reportContent'])

// 화면 모드: 'select' (섹션 선택) | 'result' (보고서 결과)
const viewMode = ref('select')

// 마크다운을 HTML로 변환
const renderedMarkdown = computed(() => {
  if (!reportContent.value) return ''
  return marked(reportContent.value)
})

const props = defineProps({
  reportData: {
    type: Object,
    required: true
  },
  projectId: {
    type: Number,
    default: null
  },
  jobIds: {
    type: Array,
    required: true
  },
  startDate: {
    type: String,
    default: null
  },
  endDate: {
    type: String,
    default: null
  },
  channels: {
    type: Array,
    default: () => []
  },
  modelType: {
    type: String,
    default: 'openai'
  }
})

const emit = defineEmits(['close', 'download', 'generate-success', 'generate-error'])

// 로딩 및 에러 상태
const isLoading = ref(false)
const errorMessage = ref('')

// 보고서 제목
const reportTitle = ref('')

// 보고서 생성 진행 상태
const isGenerating = ref(false)
const isDownloading = ref(false)
const currentTaskId = ref(null)

// 컴포넌트 언마운트 시 폴링 중지
onBeforeUnmount(() => {
  if (isPolling.value) {
    store.dispatch('report/stopPolling')
  }
})

// 폴링 완료 처리 - 다운로드 API 호출
async function handlePollingComplete(status) {
  try {
    // 다운로드 API 호출하여 보고서 내용 가져오기
    await store.dispatch('report/downloadReport', status.task_id)

    isGenerating.value = false
    viewMode.value = 'result'  // 결과 화면으로 전환

    emit('generate-success', {
      taskId: status.task_id,
      message: '보고서 생성이 완료되었습니다.',
      result: status.result
    })
  } catch (error) {
    handlePollingError(error.message || '보고서 다운로드에 실패했습니다.')
  }
}

// 폴링 에러 처리 - 섹션 선택 화면으로 돌아가기
function handlePollingError(errorMsg) {
  isGenerating.value = false
  errorMessage.value = errorMsg
  viewMode.value = 'select'  // 섹션 선택 화면으로 돌아가기
  emit('generate-error', { message: errorMsg })
}

// 섹션 선택 화면으로 돌아가기
function goBackToSelect() {
  viewMode.value = 'select'
  store.dispatch('report/clearReportContent')
  errorMessage.value = ''
}

// Word 파일 다운로드
async function downloadWord() {
  if (!currentTaskId.value) {
    errorMessage.value = 'Task ID가 없습니다.'
    return
  }

  isDownloading.value = true
  errorMessage.value = ''

  try {
    await store.dispatch('report/downloadWordReport', currentTaskId.value)
  } catch (error) {
    errorMessage.value = error.message || 'Word 파일 다운로드에 실패했습니다.'
  } finally {
    isDownloading.value = false
  }
}

// 상태 텍스트 변환
function getStatusText(status) {
  const statusMap = {
    'pending': '대기 중',
    'processing': '처리 중',
    'completed': '완료',
    'failed': '실패'
  }
  return statusMap[status] || status
}

// 탭 정의
const reportTabs = ref([
  { id: 'public', label: '공기업' },
  { id: 'private', label: '사기업' },
  { id: 'institution', label: '기관' },
  { id: 'brand', label: '브랜드' },
  { id: 'policy', label: '정책' }
])

const activeTab = ref('public')

// 공기업 섹션 정의
const publicSections = ref([
  {
    id: '1',
    title: '데이터 개요',
    subsections: [
      { id: '1.1', key: 'SEC001', title: '분석대상', description: '선택한 기간과 조건에 따라 수집된 전체 데이터를 분석 대상으로 설정하며, 주요 이슈와 여론 흐름을 종합적으로 파악할 수 있는 기초 데이터를 생성합니다.', selected: true, disabled: true, unavailable: false },
      { id: '1.2', key: 'SEC002', title: '분석기간', description: '보고서 생성 시 설정한 데이터 수집 및 분석 기간을 기준으로, 모든 분석 결과가 산출됩니다.', selected: false, disabled: false, unavailable: false },
      { id: '1.3', key: 'SEC003', title: '수집채널', description: '선택된 온라인 채널(예: 언론, 블로그 등)을 기준으로 데이터를 수집하며, 채널별 데이터 비중과 특성을 비교할 수 있는 정보가 생성됩니다.', selected: false, disabled: false, unavailable: false },
      { id: '1.4', key: 'SEC004', title: '분석 데이터', description: '선택 조건에 따라 수집된 데이터의 총량, 일평균 수집량 등 기본 통계 정보가 생성됩니다.', selected: false, disabled: false, unavailable: false },
      { id: '1.5', key: 'SEC005', title: '키워드 분포', description: '수집된 데이터에서 주요 키워드를 추출하여, 어떤 주제와 이슈가 많이 언급되었는지를 보여주는 분석 결과가 생성됩니다.', selected: false, disabled: false, unavailable: false },
      { id: '1.6', key: 'SEC006', title: '전체 감성 비율', description: '데이터 전반의 긍정·부정·중립 감성 비율을 산출하여, 전체 여론의 기본적인 정서 분포를 확인할 수 있습니다.', selected: false, disabled: false, unavailable: false },
      { id: '1.7', key: 'SEC007', title: '기간별 감성 변화', description: '시간 흐름에 따른 감성 변화를 분석하여, 특정 시점의 여론 변화 및 감성 추이를 확인할 수 있는 데이터가 생성됩니다.', selected: false, disabled: false, unavailable: false }
    ]
  },
  {
    id: '2',
    title: '이슈 요약',
    subsections: [
      { id: '2.1', key: 'SEC101', title: '채널별 요약', description: '각 수집 채널별로 주요 이슈, 키워드, 감성 특성을 요약하여 채널 간 이슈 차이를 비교할 수 있는 결과가 생성됩니다.', selected: false, disabled: false, unavailable: false },
      { id: '2.2', key: 'SEC102', title: '기간별 요약', description: '기간 단위로 주요 이슈와 변화 흐름을 정리하여, 이슈의 발생 시점과 확산 양상을 파악할 수 있습니다.', selected: false, disabled: false, unavailable: false },
      { id: '2.3', key: 'SEC103', title: '통합 이슈 정리', description: '전체 채널과 기간을 종합하여 핵심 이슈를 통합적으로 정리하고, 주요 이슈 간 연관성을 분석한다.', selected: false, disabled: false, unavailable: false }
    ]
  },
  {
    id: '3',
    title: 'Sociality Index 분석',
    subsections: [
      { id: '3.1', key: 'SEC201', title: '전체 Sociality Index 점수 결과', description: '분석 기간 전체를 기준으로 산출된 Sociality Index 종합 점수를 제시한다.', selected: false, disabled: false, unavailable: false },
      { id: '3.2', key: 'SEC202', title: '월별 Sociality Index 변화 추이', description: '월 단위 Sociality Index 변화 추이를 통해 여론 반응의 증감 흐름을 시계열로 분석한다.', selected: false, disabled: false, unavailable: false },
      { id: '3.3', key: 'SEC203', title: '주요 변동 요인 분석', description: 'Sociality Index 변화에 영향을 미친 주요 이슈 및 요인을 분석하여 변동 원인을 설명한다.', selected: false, disabled: false, unavailable: false }
    ]
  },
  {
    id: '4',
    title: '종합 인사이트',
    subsections: [
      { id: '4.1', key: 'SEC301', title: '인사이트', description: '데이터 분석 결과를 바탕으로 핵심 인사이트를 정리하고, 향후 참고 가능한 시사점을 제시한다.', selected: false, disabled: false, unavailable: true }
    ]
  },
  {
    id: '5',
    title: 'Appendix',
    subsections: [
      { id: '5.1', key: 'SEC401', title: 'Appendix', description: '세부 분석 결과, 추가 통계, 참고 자료 등 본문에 포함되지 않은 부가 정보를 제공한다.', selected: false, disabled: false, unavailable: true }
    ]
  }
])

// 다른 탭의 섹션들 (추후 확장)
const privateSections = ref([])
const institutionSections = ref([])
const brandSections = ref([])
const policySections = ref([])

// 현재 활성 탭의 섹션
const reportSections = computed(() => {
  switch (activeTab.value) {
    case 'public':
      return publicSections.value
    case 'private':
      return privateSections.value
    case 'institution':
      return institutionSections.value
    case 'brand':
      return brandSections.value
    case 'policy':
      return policySections.value
    default:
      return publicSections.value
  }
})

// 섹션 선택/해제 토글
function toggleSection(sectionId, subsectionId) {
  const section = reportSections.value.find(s => s.id === sectionId)
  if (section) {
    const subsection = section.subsections.find(sub => sub.id === subsectionId)
    if (subsection && !subsection.disabled && !subsection.unavailable) {
      subsection.selected = !subsection.selected
    }
  }
}

// 선택된 섹션 목록 계산
const selectedSections = computed(() => {
  const selected = []
  reportSections.value.forEach(section => {
    section.subsections.forEach(subsection => {
      if (subsection.selected) {
        selected.push({
          sectionId: section.id,
          sectionTitle: section.title,
          subsectionId: subsection.id,
          subsectionTitle: subsection.title,
          key: subsection.key
        })
      }
    })
  })
  return selected
})

// 선택된 섹션 코드(key) 배열 추출
const selectedSectionKeys = computed(() => {
  return selectedSections.value.map(item => item.key)
})

// 보고서 생성 API 호출 (store action 사용)
async function generateReport() {
  // 필수 값 검증
  if (!props.jobIds || props.jobIds.length === 0) {
    errorMessage.value = 'Job ID가 필요합니다.'
    emit('generate-error', { message: errorMessage.value })
    return
  }


  
  if (selectedSectionKeys.value.length === 0) {
    errorMessage.value = '최소 하나의 섹션을 선택해주세요.'
    emit('generate-error', { message: errorMessage.value })
    return
  }


  if (!reportTitle.value || reportTitle.value.trim().length === 0) {
    errorMessage.value = '보고서 제목은 필수 입력 항목입니다.'
    emit('generate-error', { message: errorMessage.value })
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    const payload = {
      job_ids: props.jobIds,
      selected_sections: selectedSectionKeys.value,
      model_type: props.modelType
    }


    

    // 선택적 파라미터 추가
    if (props.projectId) {
      payload.project_id = props.projectId
    }
    if (props.startDate) {
      payload.start_date = props.startDate
    }
    if (props.endDate) {
      payload.end_date = props.endDate
    }
    if (props.channels && props.channels.length > 0) {
      payload.channels = props.channels
    }
    if (reportTitle.value.trim()) {
      payload.report_title = reportTitle.value.trim()
    }

    const response = await store.dispatch('report/generateReport', payload)

    // 보고서 생성 시작됨 - 폴링 시작
    isLoading.value = false
    isGenerating.value = true
    currentTaskId.value = response.task_id

    // 폴링 시작
    store.dispatch('report/startPolling', {
      taskId: response.task_id,
      onComplete: handlePollingComplete,
      onError: handlePollingError
    })

  } catch (error) {
    errorMessage.value = store.state.report.error || error.message || '보고서 생성 중 오류가 발생했습니다.'
    emit('generate-error', { message: errorMessage.value })
    isLoading.value = false
  }
}
</script>

<style scoped>
textarea { font-family: 'Noto Sans KR', sans-serif; line-height: 1.5; }
pre { font-family: 'Noto Sans KR', sans-serif; white-space: pre-wrap; }

/* GPT-style markdown */
:deep(.markdown-body) {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI",
               Roboto, "Noto Sans KR", sans-serif;
  font-size: 15px;
  line-height: 1.75;
  color: #1f2937;
  word-break: keep-all;
}

/* Headings */
:deep(.markdown-body h1) {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 2rem 0 1rem;
}

:deep(.markdown-body h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.8rem 0 0.8rem;
}

:deep(.markdown-body h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1.5rem 0 0.6rem;
}

:deep(.markdown-body h4) {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 1.2rem 0 0.5rem;
}

/* Paragraph */
:deep(.markdown-body p) {
  margin: 0.75rem 0;
}

/* Strong / Em */
:deep(.markdown-body strong) {
  font-weight: 600;
}

:deep(.markdown-body em) {
  font-style: italic;
}

/* Lists */
:deep(.markdown-body ul),
:deep(.markdown-body ol) {
  margin: 0.8rem 0 0.8rem 1.25rem;
  padding: 0;
}

:deep(.markdown-body li) {
  margin: 0.4rem 0;
}

/* Inline code */
:deep(.markdown-body code) {
  background: #f3f4f6;
  color: #111827;
  padding: 0.15rem 0.35rem;
  border-radius: 4px;
  font-size: 0.9em;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* Code block */
:deep(.markdown-body pre) {
  background: #f3f4f6;
  padding: 1rem;
  border-radius: 6px;
  overflow-x: auto;
  margin: 1rem 0;
}

:deep(.markdown-body pre code) {
  background: transparent;
  padding: 0;
  font-size: 0.9rem;
}

/* Blockquote */
:deep(.markdown-body blockquote) {
  margin: 1rem 0;
  padding-left: 1rem;
  color: #4b5563;
  border-left: 4px solid #e5e7eb;
}

/* Tables */
:deep(.markdown-body table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.9rem;
}

:deep(.markdown-body th),
:deep(.markdown-body td) {
  border: 1px solid #e5e7eb;
  padding: 0.5rem 0.75rem;
  text-align: left;
}

:deep(.markdown-body th) {
  background: #f9fafb;
  font-weight: 600;
}

/* Links */
:deep(.markdown-body a) {
  color: #7c3aed;
  text-decoration: underline;
}

:deep(.markdown-body) {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI",
               Roboto, "Noto Sans KR", sans-serif;
  font-size: 15px;
  line-height: 1.75;
  color: #1f2937;
  word-break: keep-all;

  /* ⭐ GPT 핵심 */
  text-align: left;
  max-width: 1200px;
  margin: 0 auto;
}

/* Scrollbar */
:deep(.result-content-box::-webkit-scrollbar) { width: 8px; height: 8px; }
:deep(.result-content-box::-webkit-scrollbar-track) { background: #f1f5f9; border-radius: 10px; }
:deep(.result-content-box::-webkit-scrollbar-thumb) { background: #cbd5e1; border-radius: 10px; }
:deep(.result-content-box::-webkit-scrollbar-thumb:hover) { background: #94a3b8; }
:deep(.result-content-box) { scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9; }
</style>